import { Component, ChangeDetectionStrategy, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { OiqLayoutPageSingle } from '@oiq-ui-sdk/layout';
import { AuthFacade } from '@core/auth/auth.facade';
import { DEVELOPER_ALLOWED_URLS, DEVELOPER_HOME_URL } from '@core/auth/auth.state';
import { environment } from '@environments/environment';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-settings-layout',
  imports: [RouterModule, OiqLayoutPageSingle],
  templateUrl: './settings-layout.component.html',
  styleUrls: ['./settings-layout.component.scss'],
})
export class SettingsLayoutComponent {
  readonly #auth = inject(AuthFacade);
  readonly #router = inject(Router);

  readonly apiDocsUrl = environment.apiDocsUrl;
  readonly isDeveloper = this.#auth.isDeveloper;
  readonly isApiKeysPage = toSignal(
    this.#router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.#router.url.startsWith(DEVELOPER_HOME_URL)),
    ),
    { initialValue: this.#router.url.startsWith(DEVELOPER_HOME_URL) },
  );

  readonly #allNavItems: {
    label: string;
    route: string;
    svg: string;
    devOnly?: boolean;
    adminOnly?: boolean;
  }[] = [
    {
      label: 'Users',
      route: '/admin/settings/users',
      svg: '/assets/icons/users.svg',
      adminOnly: true,
    },
    {
      label: 'Brands',
      route: '/admin/settings/brands',
      svg: '/assets/icons/brands-icon.svg',
    },
    {
      label: 'Contacts',
      route: '/admin/settings/contacts',
      svg: '/assets/icons/user-focus.svg',
    },
    {
      label: 'Organization',
      route: '/admin/settings/organization',
      svg: '/assets/icons/building-icon.svg',
    },
    {
      label: 'Plans',
      route: '/admin/settings/plans',
      svg: '/assets/icons/dolar-icon.svg',
    },
    {
      label: 'API Keys',
      route: DEVELOPER_HOME_URL,
      svg: '/assets/icons/key-icon.svg',
    },
    {
      label: 'Integrations',
      route: '/admin/settings/integrations',
      svg: '/assets/icons/share-network-icon.svg',
      devOnly: true,
    },
  ];

  readonly navItems = computed(() => {
    if (this.#auth.isDeveloper()) {
      return this.#allNavItems.filter((item) =>
        DEVELOPER_ALLOWED_URLS.some((url) => item.route.startsWith(url)),
      );
    }
    const { canAccessApiKeys, canManage } = this.#auth.permissions();
    return this.#allNavItems.filter(
      (item) =>
        !item.devOnly &&
        (canAccessApiKeys || item.route !== DEVELOPER_HOME_URL) &&
        (!item.adminOnly || canManage),
    );
  });
}
